"use client";
import { useEffect, useState } from "react";
import { getImageBuildById } from "../services/mediaBuilder/imageBuildsService";
import { getPreviewUrl } from "../mediaBuilder/media";
import type { Translationtype } from "utils";

interface BackgroundTemplatePreviewsProps {
  backgroundTemplates: any[];
  t: Translationtype;
}

export function BackgroundTemplatePreviews({ backgroundTemplates, t }: BackgroundTemplatePreviewsProps) {
  const [previews, setPreviews] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!backgroundTemplates?.length) {
      setPreviews([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const loadPreviews = async () => {
      const urls: string[] = [];
      const templatesToLoad = backgroundTemplates.slice(0, 5);
      for (const tpl of templatesToLoad) {
        try {
          const build = await getImageBuildById(tpl.id);
          if (build?.previewId) {
            const url = await getPreviewUrl(build.previewId);
            if (url) urls.push(url);
          }
        } catch (e) {
          console.error('Failed to load preview for template', tpl.id, e);
        }
      }
      if (!cancelled) {
        setPreviews(urls);
        setLoading(false);
      }
    };

    loadPreviews();
    return () => { cancelled = true; };
  }, [backgroundTemplates]);

  if (!backgroundTemplates?.length) {
    return <p className="text-sm text-gray-400 mt-2">{t("Reservation.noBackgroundSelected")}</p>;
  }

  return (
    <div className="mt-3">
      <p className="text-sm text-gray-700 font-medium mb-2">
        🖼️ {t("Reservation.backgroundsSelected", { count: backgroundTemplates.length })}
      </p>
      {loading ? (
        <div className="flex gap-2">
          {Array.from({ length: Math.min(backgroundTemplates.length, 5) }).map((_, i) => (
            <div key={i} className="h-16 w-16 rounded-lg bg-gray-100 border border-gray-200 animate-pulse" />
          ))}
        </div>
      ) : previews.length > 0 ? (
        <div className="flex gap-2 flex-wrap">
          {previews.map((url, i) => (
            <img
              key={i}
              src={url}
              alt={t("Reservation.backgroundTemplatePreviewAlt", { index: i + 1 })}
              className="h-16 w-16 rounded-lg object-cover border border-gray-200 shadow-sm"
            />
          ))}
          {backgroundTemplates.length > 5 && (
            <span className="h-16 w-16 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-xs font-semibold text-gray-500">
              +{backgroundTemplates.length - 5}
            </span>
          )}
        </div>
      ) : null}
    </div>
  );
}
